// client/src/components/Signup.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import '../CSS/signup.css';
import { auth } from '../../firebase';
import { signInAnonymously } from 'firebase/auth';
import { getFirestore, doc, setDoc, serverTimestamp } from 'firebase/firestore';
// import api from '../api'; // on évite l’instance pour supprimer tout retry implicite
// import { sanitize } from '../utils/sanitize';

import { REGEX as PASSWORD_REGEX, POLICY as PASSWORD_POLICY } from '../../policies/passwordPolicy';
import PasswordField from '../shared/PasswordField';
import LegalAccordion from '../shared/LegalAccordion';
import { FaUser, FaBuilding } from 'react-icons/fa';
import { BrandMark } from '../shared/BrandLogo';

// Helpers sûrs
const sanitizeEmail = (s) =>
  (s || '')
    .normalize('NFKC')
    .replace(/\s+/g, '') // retire seulement les espaces
    .toLowerCase();

const normalizeRole = (r) => (r === 'commercantpending' ? 'commercantpending' : 'client');

// --- Base API robuste (CF direct vs Domaine) ---
const RAW_API =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) ||
  (process.env.NODE_ENV === 'production'
    ? window.location.origin
    : 'http://localhost:5099');

const API_BASE = RAW_API.replace(/\/+$/, '');
const isCFDirect = /cloudfunctions\.net\/[^/]+$/i.test(API_BASE);

const SIGNUP_ENDPOINT = isCFDirect
  ? `${API_BASE}/auth/signup`
  : `${API_BASE}/api/auth/signup`;

const CSRF_ENDPOINT = isCFDirect
  ? `${API_BASE}/csrf-token`
  : `${API_BASE}/api/csrf-token`;

const Signup = () => {
  const [email, setEmail]                     = useState('');
  const [password, setPassword]               = useState('');
  const [role, setRole]                       = useState('client');
  const [message, setMessage]                 = useState(null);
  const [isGuestLoading, setIsGuestLoading]   = useState(false);
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [acceptedCGU, setAcceptedCGU]         = useState(false);

  const isPwdValid = PASSWORD_REGEX.test(password || '');

  const navigate = useNavigate();
  const location = useLocation();
  const hasEmailInQuery = Boolean(new URLSearchParams(location.search).get('email'));

  // AbortController pour empêcher toute répétition réseau lors d’un démontage
  const abortRef = useRef(null);

  useEffect(() => {
    const params     = new URLSearchParams(location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setEmail(emailParam);
      setRole('client');
    }
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [location.search]);

  const getCookie = (name) => {
    const m = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([$?*|{}\]\\^])/g, '\\$1') + '=([^;]*)')
    );
    return m ? decodeURIComponent(m[1]) : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSignupLoading) return; // anti double-clic

    setIsSignupLoading(true);
    setMessage(null);

    if (!isPwdValid) {
      setMessage({ text: PASSWORD_POLICY.message, isSuccess: false });
      setIsSignupLoading(false);
      return;
    }

    const safeEmail    = sanitizeEmail(email);
    const safePassword = password; // ne pas sanitize le mdp
    const safeRole     = normalizeRole(role);

    try {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      // CSRF (non bloquant si non requis)
      try {
        await fetch(CSRF_ENDPOINT, { credentials: 'include' });
      } catch {}

      const xsrf = getCookie('XSRF-TOKEN');

      const res = await fetch(SIGNUP_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(xsrf ? { 'X-CSRF-Token': xsrf } : {})
        },
        body: JSON.stringify({
          email: safeEmail,
          password: safePassword,
          role: safeRole,
        }),
        signal: abortRef.current.signal,
      });

      let data = {};
      try { data = await res.json(); } catch { /* vide/HTML possible en erreur */ }

      if (!res.ok) {
        const errMsg = data?.error
          || (data?.errors && data.errors.map((e) => e.msg).join(', '))
          || `Erreur (${res.status}) lors de l’inscription`;
        throw new Error(errMsg);
      }

      setMessage({ text: 'Inscription réussie ! ✅', isSuccess: true });
      try { sessionStorage.setItem('ecobill_cgu_pending', safeRole); } catch {}
      setTimeout(() => {
        navigate(`/logingeneral?email=${encodeURIComponent(safeEmail)}`);
      }, 1200);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Erreur lors de l’inscription :', err);
      setMessage({ text: err?.message || 'Erreur réseau, réessayez.', isSuccess: false });
    } finally {
      setIsSignupLoading(false);
    }
  };

  const handleGuestSignup = async () => {
    if (isGuestLoading || isSignupLoading) return;
    setIsGuestLoading(true);
    try {
      const { user } = await signInAnonymously(auth);
      const db       = getFirestore();
      await setDoc(doc(db, 'users', user.uid), {
        uid:       user.uid,
        role:      'client',
        createdAt: serverTimestamp()
      });
      localStorage.setItem('userRole', 'client');
      setMessage({ text: 'Inscription en mode invité ! ✅', isSuccess: true });
      setTimeout(() => navigate('/clientdocs'), 1000);
    } catch (err) {
      console.error('Erreur signup invité :', err);
      setMessage({ text: 'Impossible en mode invité, réessayez.', isSuccess: false });
    } finally {
      setIsGuestLoading(false);
    }
  };

  const disableSubmit = isSignupLoading || isGuestLoading || !isPwdValid || !acceptedCGU;
  const disableGuest  = isGuestLoading || isSignupLoading;

  const roleDisabled = isGuestLoading || isSignupLoading;

  return (
    <div className="eb-auth">
      <div className="eb-auth__card">
        <div className="eb-auth__brand"><BrandMark /></div>
        <h1 className="eb-auth__title">Inscription</h1>
        <p className="eb-auth__subtitle">Créez votre compte Ecobill</p>

        <form className="eb-auth__form" onSubmit={handleSubmit} noValidate>
          <div className="signup-roles" role="radiogroup" aria-label="Type de compte">
            {[
              { value: 'client',             label: 'Utilisateur', icon: <FaUser size={20} /> },
              { value: 'commercantpending',  label: 'Entreprise',  icon: <FaBuilding size={20} /> },
            ].map(({ value, label, icon }) => {
              const selected = role === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`signup-role${selected ? ' is-selected' : ''}`}
                  onClick={() => !roleDisabled && setRole(value)}
                  disabled={roleDisabled}
                >
                  <span className="signup-role__icon">{icon}</span>
                  {label}
                </button>
              );
            })}
          </div>

          <div className="eb-field">
            <label className="eb-label" htmlFor="signup-email">Adresse e-mail</label>
            <input
              id="signup-email"
              className="eb-input"
              type="email"
              placeholder="vous@exemple.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={hasEmailInQuery || isGuestLoading || isSignupLoading}
              autoComplete="email"
              inputMode="email"
              spellCheck={false}
            />
          </div>

          <div className="eb-field">
            <label className="eb-label" htmlFor="signup-password">Mot de passe</label>
            <PasswordField
              id="signup-password"
              className="eb-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isGuestLoading || isSignupLoading}
              required
              autoComplete="new-password"
              pattern={PASSWORD_REGEX.source}
              title={PASSWORD_POLICY.message}
              aria-invalid={!!password && !isPwdValid}
              spellCheck={false}
            />
          </div>

          <div>
            <LegalAccordion
              docId="cgu"
              checked={acceptedCGU}
              onChange={setAcceptedCGU}
            />
            <p className="signup-legal-note">
              En vous inscrivant, vous reconnaissez également avoir pris connaissance de notre{' '}
              <Link to="/legal/politique-confidentialite" target="_blank" rel="noopener noreferrer">
                Politique de confidentialité
              </Link>.
            </p>
          </div>

          {message && (
            <div className={`eb-alert ${message.isSuccess ? 'eb-alert--success' : 'eb-alert--danger'}`} role="alert">
              {message.text}
            </div>
          )}

          <button
            type="submit"
            className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block"
            disabled={disableSubmit}
          >
            {isSignupLoading ? <span className="eb-spinner eb-spinner--sm eb-spinner--light" aria-hidden="true" /> : null}
            {isSignupLoading ? 'Inscription…' : "S'inscrire"}
          </button>

          {!hasEmailInQuery && (
            <>
              <div className="eb-auth__sep">ou</div>
              <button
                type="button"
                className="eb-btn eb-btn--secondary eb-btn--lg eb-btn--block"
                onClick={handleGuestSignup}
                disabled={disableGuest}
              >
                {isGuestLoading ? <span className="eb-spinner eb-spinner--sm" aria-hidden="true" /> : null}
                {isGuestLoading ? 'Chargement…' : 'Continuer en mode invité'}
              </button>
            </>
          )}
        </form>

        <div className="eb-auth__footer">
          Déjà un compte ? <Link to="/logingeneral">Se connecter</Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
